<template>
  <v-alert
    type="warning"
    variant="tonal"
    density="compact"
    :icon="false"
    class="mb-2 person-match-candidates"
  >
    <div class="text-body-2 mb-1 person-match-candidates__text">
      This person may already exist — did you mean one of these?
    </div>
    <v-list density="compact" bg-color="transparent" class="pa-0">
      <v-list-item
        v-for="candidate in candidates"
        :key="candidate.id"
        class="px-0 person-match-candidates__item"
        @click="$emit('select', candidate)"
      >
        <template #prepend>
          <v-chip
            size="x-small"
            label
            variant="tonal"
            class="mr-2 person-match-candidates__tier"
            :class="{
              'person-match-candidates__tier--strong': candidate.tier === 'A',
              'person-match-candidates__tier--weak': candidate.tier === 'C',
            }"
          >
            {{ tierLabel(candidate.tier) }}
          </v-chip>
        </template>
        <!-- The email disambiguates two people with the same name, which is
             the case this panel exists for. Rendered only when present — the
             server may not send it (see IPersonMatchCandidate), and an empty
             second line on every row would be worse than no email at all.
             As a SUBTITLE rather than a second element of our own: it shares the
             title's content column, so it left-aligns with the name without any
             layout of ours, and the chip stays in #prepend beside both. -->
        <v-list-item-title>{{ candidate.descr }}</v-list-item-title>
        <v-list-item-subtitle v-if="candidate.email">
          {{ candidate.email }}
        </v-list-item-subtitle>
      </v-list-item>
    </v-list>
  </v-alert>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { PropType } from 'vue'
import type { IPersonMatchCandidate } from '~/types/person/models'

/**
 * ⚠ THREE tiers, not two — see IPersonMatchCandidate for the server's definition.
 * A "C" shares NO identity key at all, only a name the server judged similar, so
 * it must not read the same as a "B", which shares a real email, phone or exactly
 * equal name. Collapsing the two overstates the weakest candidates in the list.
 */
const TIER_LABELS: Record<IPersonMatchCandidate['tier'], string> = {
  A: 'Strong match',
  B: 'Possible match',
  C: 'Similar name',
}

/**
 * "Did you mean…?" panel for the person forms (#113). Purely presentational: it
 * lists the server's ranked candidates and emits `select` when one is clicked.
 * The parent decides what selecting does — navigate to the existing person on
 * the direct form, or attach it to the contact in the "NEW person" subentity
 * dialog. Populated via the personMatchPanel field — see cynqueModules/personMatch.ts.
 */
export default defineComponent({
  name: 'PersonMatchCandidates',
  props: {
    candidates: {
      type: Array as PropType<IPersonMatchCandidate[]>,
      default: () => [],
    },
  },
  emits: ['select'],
  methods: {
    /**
     * ⚠ The `??` is a deliberate safe default, NOT dead code. A tier this build
     * has never heard of renders as the middle label rather than as `undefined`
     * in the chip. The type is the real guard — register a new tier there, and
     * TypeScript will require a label here.
     */
    tierLabel(tier: IPersonMatchCandidate['tier']): string {
      return TIER_LABELS[tier] ?? 'Possible match'
    },
  },
})
</script>

<style scoped>
/* Matched to the address-verification panel in EntityMaskForm.vue, so the two
   display-only advisory panels read as one family rather than as two features
   that happen to use v-alert. The rules below are ported from
   `.emf-address-verification`; the reasoning for each is recorded there, and the
   two should move together. Only the button rules are left behind — this panel
   has none, its candidates being list items. */

.person-match-candidates {
  /* Space between the field underlines above and the top of the block. MARGIN,
     not padding: padding-top would grow the box downward and leave its top edge,
     and so the gap, exactly where it was. */
  margin-top: 8px;

  /* ⚠ A REAL border, deliberately NOT VAlert's `border` prop. That prop renders
     a separate absolutely-positioned `.v-alert__border` layer stacked over the
     `.v-alert__underlay` that paints the tint — two independent boxes that only
     coincide by construction, and with an 8px edge they did not, so the rule
     visibly overshot the top of the tinted block. A real border shares the
     element's own box with the tint, so the two cannot disagree. */
  border-inline-start: 6px solid currentColor;
  /* Softened to sit with the 0.06 tint; at full strength it read as a hard rule.
     Set AFTER the shorthand so a browser without color-mix keeps a solid border
     rather than losing it. */
  border-inline-start-color: color-mix(in srgb, currentColor 65%, transparent);
}

/* Soften the tint on this panel's OWN underlay only — not via
   `--v-activated-opacity` on the alert root, which inherits and would drag every
   tonal child down with it.
   ⚠ 0.11 here where the address panel sits at 0.06, deliberately: this panel
   lost its icon, so the tint and the left rule are the only things carrying its
   presence, and at 6% it read as barely there. The two are no longer identical —
   if they should be, this is the line to change. */
.person-match-candidates :deep(.v-alert__underlay) {
  opacity: 0.11;
}

/* Neutral text, so the message is not the warning colour sitting on its own
   warning tint. Scoped to the TEXT block rather than `.v-alert__content`: the
   candidate list lives in the content too, and its tier chips carry their own
   semantic colour that recolouring the container would fight. */
.person-match-candidates__text {
  color: rgb(var(--v-theme-on-surface));
}

/* The candidate's NAME is the payload — the thing the user is here to read — so
   it is neutral and carries the weight, exactly as the address panel's suggested
   address block does. It rendered in the warning hue before, which put the
   semantic colour on the content instead of on the frame. */
.person-match-candidates :deep(.v-list-item-title) {
  color: rgb(var(--v-theme-on-surface));
  font-weight: 500;
}

/* ⚠ The subtitle needs its colour set for the SAME reason the title did: left
   alone it inherits the alert's `currentColor` and comes out amber, putting the
   semantic hue back on the content. Vuetify's own medium-emphasis opacity is
   what mutes it, so the email reads as corroborating detail under the name
   rather than as a second identifier competing with it. */
.person-match-candidates :deep(.v-list-item-subtitle) {
  color: rgba(var(--v-theme-on-surface), 0.65);
}

/* ⚠ NO SEMANTIC COLOUR ON THE TIER CHIP, and this is the rule the panel most
   needed. It was `color="error"` for a strong match and `color="warning"` for a
   possible one — so "Possible match" was amber on the panel's own amber tint and
   all but disappeared, while "Strong match" red fought the hue the border and
   icon already carry.
   The address panel settled this question already: it marks the words that
   changed by WEIGHT alone, "no colour, so it does not compete with the semantic
   hue already carried by the border, icon and buttons". Same answer here — the
   chip is neutral, and a strong match is heavier rather than redder. */
.person-match-candidates :deep(.person-match-candidates__tier) {
  color: rgba(var(--v-theme-on-surface), 0.7);
}
.person-match-candidates :deep(.person-match-candidates__tier--strong) {
  color: rgb(var(--v-theme-on-surface));
  font-weight: 700;
}
/* Tier C sits BELOW the 0.7 base, completing a 0.5 / 0.7 / 1.0-plus-bold ramp, so
   the three tiers separate by weight alone — the same answer the address panel
   reached, and the reason nothing here is coloured. */
.person-match-candidates :deep(.person-match-candidates__tier--weak) {
  color: rgba(var(--v-theme-on-surface), 0.5);
}
</style>
